import { EncoderDecoder } from '@iotize/common/converter/api';
import { TapVersionConverter } from '@iotize/tap';
import { Ipv4StringConverter, MacAddressStringConverter } from '@iotize/tap/client/impl';
import { WifiMode } from '@iotize/tap/service/all';
import { converters, createEnumConverter } from '@iotize/tap/service/core';
import {
    LoginCredentialConverter,
    LowPowerOptimizationLevel,
    MultiRequestFrameConverter,
    MultiResponseFrameConverter,
    NfcPairingMode,
} from '@iotize/tap/service/impl/interface';
import { ScramLoginParamsConverter, ScramLoginResponseBodyConverter } from '@iotize/tap/service/impl/scram';
import { VariableTypeConverter } from '@iotize/tap/service/impl/variable';

export const ascii = converters.ascii;
export const uint8 = converters.uint8;
export const uint16 = converters.uint16;
export const uint32 = converters.uint32
export const boolean = converters.boolean;
export const targetProtocol = converters.targetProtocol;
export const hostProtocol = converters.hostProtocol;
export const hostProtocols = converters.hostProtocolArray;

export const loginCredential = new LoginCredentialConverter();
export const wifiMode = createEnumConverter<WifiMode>(WifiMode, 1);
export const initScramLoginParam = new ScramLoginParamsConverter();
export const initScramLoginResponse = new ScramLoginResponseBodyConverter();
export const nfcPairingMode = createEnumConverter<NfcPairingMode>(NfcPairingMode, 1); //new NfcPairingModeConverter();
export const powerOptimizationLevel = createEnumConverter<LowPowerOptimizationLevel>(LowPowerOptimizationLevel, 1); //new LowPowerOptimizationLevelConverter();
export const tapVersion = new TapVersionConverter();
export const variableType = new VariableTypeConverter();
export const multiRequests = new MultiRequestFrameConverter();
export const multiResponses = new MultiResponseFrameConverter();
export const macAddress = new MacAddressStringConverter();
export const ipv4String = new Ipv4StringConverter();

export function enumIntToEnumStringConverter<OT extends string, T extends string | number>(converter: EncoderDecoder<T, Uint8Array>, mapping: Record<OT, T>): EncoderDecoder<OT, Uint8Array> {
    return {
        decode: input => getMappingKey(reverseMapping(mapping), converter.decode(input)),
        encode: input => converter.encode(getMappingKey(mapping, input))
    }
}

export function reverseMapping<OT extends string | number | symbol, T extends string | number | symbol>(mapping: Record<T, OT>): Record<OT, T> {
    const result: Record<any, T> = {};
    // tslint:disable-next-line: forin
    for (const key in mapping) {
        result[mapping[key]] = key;
    }
    return result;
}

function getMappingKey<TKey extends string | number, TValue>(mapping: Record<TKey, TValue>, key: TKey) {
    if (!(key in mapping)) {
        throw new Error(`Missing key ${key}`);
    }
    return mapping[key];
}

export function enumIntToEnumStringArrayConverter<OT extends string, T extends string | number>(converter: EncoderDecoder<T[], Uint8Array>, mapping: Record<OT, T>): EncoderDecoder<OT[], Uint8Array> {
    return {
        decode: input => converter.decode(input).map(v => getMappingKey(reverseMapping(mapping), v)),
        encode: input => {
            if (!input) {
                input = [];
            }
            return converter.encode(input.map(v => getMappingKey(mapping, v)));
        }
    }
}

export const stringSementicVersion = {
    encode: (v: string) => {
        return tapVersion.encode(versionStringToVersionModel(v));
    },
    decode: (v: Uint8Array) => {
        const vData = tapVersion.decode(v);
        return `${vData.major}.${vData.minor}.${vData.patch}`;
    }
}


function versionStringToVersionModel(version: string) {
    const parts = version.split('.');
    return {
        major: parts.length > 0 ? parseInt(parts[0], 10) : 0,
        minor: parts.length > 1 ? parseInt(parts[1], 10) : 0,
        patch: parts.length > 2 ? parseInt(parts[2], 10) : 0
    };
}